import type { RoleKeyRow, RoleMapRow } from '@/api/console';

/**
 * EAA-1837: view-model types for the `/console/role-map` "Liên kết vai trò"
 * TABLE. This module replaced the
 * EAA-1817 coverage-board projections (`AppBoardItem`, `RoleChipVM`,
 * `RoleRowVM`, `CoverageCell`) — they described chips inside a per-app editor
 * and have no meaning in a flat table. `StandardRole` and the `RoleMapScope`
 * concept are carried over from that screen; `StandardRole`'s SOURCE later
 * changed (see `standardRolesFromRoleKeys`) but the type and its consumers
 * did not.
 */

/** A "vai trò chuẩn" (role_key) option for the Add-link picker. `levelIds`
 * stays on the type for API stability but is always empty now — see
 * `standardRolesFromRoleKeys`'s docstring for why the source changed. */
export interface StandardRole {
    role_key: string;
    name: string;
    description: string;
    levelIds: string[];
    isActive: boolean;
}

/**
 * EAA-1817 → EAA-1837: which slice of `GET /role-maps` the table shows.
 * `default` = ecosystem-wide rows (`organization_id === null`); `org` = the
 * acting org's own override rows. The design's "Phạm vi: Mặc định ▾" toolbar
 * dropdown is this switch.
 */
export type RoleMapScope = 'default' | 'org';

/**
 * EAA-1837: ONE VISUAL TABLE ROW — a `(role_key, application)` pair, holding
 * EVERY underlying `RoleMap` id that shares it.
 *
 * This grouping is the load-bearing part of the screen. A "multi-role" mapping
 * (`teacher_lead → lms: [teacher, admin_class]`) is stored as TWO RoleMap rows
 * sharing one `(role_key, application)`; the design renders them as one row
 * with two chips, and its checkbox therefore selects BOTH ids. `ids` is always
 * the complete list — never `ids[0]`, never a "primary" id. Collapsing a group
 * to a single id is precisely the "only the first role survives" bug class
 * EAA-1828 fixed on the provisioning-push side, and the EAA-1837 CSV export's
 * own server comments warn against reintroducing it in this UI.
 */
export interface RoleMapGroup {
    /** `${application}::${role_key}` — the selection key and React key. */
    key: string;
    role_key: string;
    application: string;
    /** Registry display name for `application`, or the code when unknown. */
    appName: string;
    /** EVERY RoleMap id in this group. Bulk actions send all of them. */
    ids: string[];
    /** One entry per underlying row, in server order — rendered as chips. */
    appRoles: RoleMapGroupAppRole[];
    /** `organization_id` shared by the group's rows (`null` = Mặc định). */
    organization_id: string | null;
    /** True when ANY row in the group is stale — the row reads "Tạm dừng". */
    isStale: boolean;
    /** Accounts on this `role_key` in the acting org (see `useAccountCounts`). */
    accountCount: number;
}

export interface RoleMapGroupAppRole {
    id: string;
    /** `app_role` key, or `—` when the catalog role was deleted outright. */
    app_role: string;
    /**
     * EAA-1817: the app role's DISPLAY name — what the chip PRINTS. The key
     * moves into the chip's tooltip (design 2026-08-24: "mã là thứ dành cho
     * tích hợp, người quản trị đọc theo tên").
     *
     * Falls back to `app_role` so a chip is never blank, which also means the
     * two can be equal — the tooltip is still worth showing, because a reader
     * cannot tell from the chip alone whether the label IS the key.
     */
    app_role_name: string;
    /** Per-chip staleness — a healthy group can still hold one dead chip. */
    stale: boolean;
}

/** Sortable columns. Matches the design's three sort chevrons exactly (Role
 *  chuẩn / Ứng dụng / Tài khoản); the chip and status columns have none. */
export type RoleMapSortField = 'role_key' | 'application' | 'accounts';
export type RoleMapSortDirection = 'asc' | 'desc';

/** "Tình trạng" filter values — the design's Bình thường / Tạm dừng pair. */
export type RoleMapStatusFilter = 'normal' | 'stale';

/** Toggleable columns, in the order the design's "Cột" menu lists them.
 *  `role_key` is pinned (see `ROLE_MAP_COLUMNS`). */
export type RoleMapColumnKey = 'role_key' | 'application' | 'app_roles' | 'scope' | 'status';

export interface RoleMapColumnSpec {
    key: RoleMapColumnKey;
    label: string;
    /** Pinned columns render a checked-but-inert entry in the "Cột" menu. */
    pinned?: boolean;
    /** Hidden until the operator turns it on ("Phạm vi" in the design). */
    hiddenByDefault?: boolean;
}

/**
 * EAA-1837: the "Cột" menu's exact contents, verbatim from the design's own
 * column menu — Role chuẩn / Ứng dụng / Vai trò trong ứng dụng /
 * Phạm vi / Tình trạng. "Tài khoản" is deliberately absent because the design's
 * menu does not list it (it sits with the checkbox and "⋯" columns as
 * structural, non-toggleable). "Role chuẩn" is `pinned`: it is the row's
 * identity, so hiding it would leave rows with nothing to identify them.
 */
export const ROLE_MAP_COLUMNS: RoleMapColumnSpec[] = [
    { key: 'role_key', label: 'Role chuẩn', pinned: true },
    { key: 'application', label: 'Ứng dụng' },
    { key: 'app_roles', label: 'Vai trò trong ứng dụng' },
    { key: 'scope', label: 'Phạm vi', hiddenByDefault: true },
    { key: 'status', label: 'Tình trạng' },
];

export const DEFAULT_VISIBLE_COLUMNS: RoleMapColumnKey[] = ROLE_MAP_COLUMNS.filter(
    (column) => !column.hiddenByDefault
).map((column) => column.key);

/**
 * EAA-1817: "vai trò chuẩn" options for the Add-link picker, now sourced from
 * the server's governed role-key vocabulary (`GET /api/v1/role-keys`) instead
 * of derived from the acting org's own Levels. The old Levels-only derivation
 * hid every role_key the ecosystem already governs but this org has not yet
 * authored a Level for — a real gap, since the picker's whole point is
 * offering the standard vocabulary, not just this org's slice of it.
 *
 * `roleKeys` is expected pre-sorted by `sort_order` then `key` (the server's
 * documented contract for this endpoint) — no client re-sort here.
 *
 * Two rules preserved from the old behaviour:
 * - rows whose `role_key` is NOT in the vocabulary still render in the table
 *   (portal-api deliberately leaves `role_key` a free field so a retired key
 *   orphans its rows rather than cascading a delete) — this function only
 *   feeds the Add-link picker and the "no vai trò chuẩn" empty state, and
 *   never filters `RoleMapRow`s, so that invariant holds untouched.
 * - an EMPTY vocabulary (nothing governed yet) falls back to the distinct
 *   `role_key`s already present on `rows`, so a screen with real, working
 *   links never renders the "chưa có vai trò chuẩn nào" empty state just
 *   because nobody has populated the vocabulary table.
 */
export function standardRolesFromRoleKeys(
    roleKeys: RoleKeyRow[],
    rows: RoleMapRow[]
): StandardRole[] {
    if (roleKeys.length > 0) {
        return roleKeys.map((rk) => ({
            role_key: rk.key,
            name: rk.name,
            description: rk.description,
            levelIds: [],
            isActive: rk.is_active,
        }));
    }
    const seen = new Set<string>();
    const fallback: StandardRole[] = [];
    for (const row of rows) {
        if (seen.has(row.role_key)) continue;
        seen.add(row.role_key);
        fallback.push({
            role_key: row.role_key,
            name: row.role_key,
            description: '',
            levelIds: [],
            isActive: true,
        });
    }
    return fallback;
}
